/**
 * AI Memory section — shared between the Account knowledge base page and
 * the Property detail KB tab. Renders the markdown stored at the given
 * endpoint with an inline edit toggle. Pulse appends suggested facts to
 * this same surface when the host clicks "Yes, add to KB" on a Memory
 * briefing item (Task #2361 follow-up).
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { BrainCircuit, Edit2, Eye, Lock, Save } from "lucide-react";
import ReactMarkdown from "react-markdown";

interface AIMemorySectionProps {
  endpoint: string;
  queryKey: ReadonlyArray<unknown>;
  canEdit: boolean;
  title?: string;
  description?: string;
  testIdPrefix: string;
  /** Unless explicitly true the section renders a locked placeholder and skips the network call (fail closed). */
  aiDraftingAllowed?: boolean;
}

export function AIMemorySection({
  endpoint,
  queryKey,
  canEdit,
  title = "AI Memory",
  description = "PadPulse stores remembered facts here. You can edit them anytime.",
  testIdPrefix,
  aiDraftingAllowed = false,
}: AIMemorySectionProps) {
  const { toast } = useToast();
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<string>("");

  const memoryQuery = useQuery<{ content: string | null }>({
    queryKey,
    enabled: aiDraftingAllowed === true,
    queryFn: async () => {
      const res = await fetch(endpoint, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load AI memory");
      return res.json();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to save AI memory");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setEditMode(false);
      toast({ title: "AI Memory saved" });
    },
    onError: () => {
      toast({ title: "Failed to save AI Memory", variant: "destructive" });
    },
  });

  const content = memoryQuery.data?.content ?? "";

  if (!aiDraftingAllowed) {
    return (
      <div className="mt-6 border rounded-lg bg-white" data-testid={`${testIdPrefix}-container`}>
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <BrainCircuit className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-500" data-testid={`${testIdPrefix}-heading`}>
            {title}
          </h3>
        </div>
        <div className="px-4 py-6 flex flex-col items-center gap-2 text-center">
          <Lock className="h-5 w-5 text-gray-300" />
          <p className="text-xs text-gray-400" data-testid={`${testIdPrefix}-locked`}>
            AI Drafting is not available on your current plan. Upgrade to enable AI Memory, Suggest, and Improve.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 border rounded-lg bg-white" data-testid={`${testIdPrefix}-container`}>
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <BrainCircuit className="h-4 w-4 text-brand" />
          <h3 className="text-sm font-semibold text-gray-900" data-testid={`${testIdPrefix}-heading`}>
            {title}
          </h3>
        </div>
        {canEdit && !memoryQuery.isLoading && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              if (editMode) {
                setEditMode(false);
              } else {
                setDraft(content);
                setEditMode(true);
              }
            }}
            data-testid={`${testIdPrefix}-toggle-edit`}
          >
            {editMode ? (
              <>
                <Eye className="h-3.5 w-3.5 mr-1" /> View
              </>
            ) : (
              <>
                <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
              </>
            )}
          </Button>
        )}
      </div>
      <div className="px-4 py-3">
        <p className="text-xs text-gray-500 mb-2">{description}</p>
        {memoryQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : editMode && canEdit ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={8}
              className="font-mono text-xs"
              placeholder="No remembered facts yet."
              data-testid={`${testIdPrefix}-editor`}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditMode(false)}
                disabled={saveMutation.isPending}
                data-testid={`${testIdPrefix}-cancel`}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-brand hover:bg-brand-hover"
                onClick={() => saveMutation.mutate(draft)}
                disabled={saveMutation.isPending}
                data-testid={`${testIdPrefix}-save`}
              >
                <Save className="h-3.5 w-3.5 mr-1" />
                {saveMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : content ? (
          <div
            className="prose prose-sm max-w-none border rounded-md p-3 bg-gray-50 min-h-[80px]"
            data-testid={`${testIdPrefix}-rendered`}
          >
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        ) : (
          <div
            className="text-xs text-gray-400 italic border border-dashed rounded-md p-4 text-center"
            data-testid={`${testIdPrefix}-empty`}
          >
            No remembered facts yet. PadPulse will suggest some as it learns from your activity.
          </div>
        )}
      </div>
    </div>
  );
}
